import { Platform, StyleSheet } from 'react-native';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StackActions } from '@react-navigation/native';

import { FormProvider } from 'react-hook-form';
import { Toast, Text, colors, Button, UserIcon, spacing, Link, FormInput } from '@pletly/ui';
import { Password } from '../../components/Password';

import { AuthContainer } from '../../components/AuthContainer/AuthContainer';
import { withTranslation } from '../../hoc/withTranslation';
import { useLogin } from './components/useLogin';
import { RootStackScreenProps } from '../../app/UnauthenticatedApp/types';
import { REGISTRATION_ENABLED } from '../../consts';

function Login({ route: { params }, navigation: { setParams } }: RootStackScreenProps<'Login'>) {
	const { t } = useTranslation();
	const { formMethods, isSending, onSubmit } = useLogin();

	useEffect(() => {
		if (params?.passwordReset) {
			Toast.show({
				type: 'success',
				text1: t('login.password-updated'),
				text2: t('login.your-password-has-been-successfully-changed'),
			});

			setParams({ passwordReset: undefined });
		}
	}, [t, setParams, params?.passwordReset]);

	return (
		<AuthContainer
			title={`${t('login.welcome-back')}!`}
			subtitle={t('login.to-keep-connected-with-us-please-login-to-your-account')}
		>
			<FormProvider {...formMethods}>
				{Platform.OS === 'web' ? (
					<>
						<Text category="h1" status="primary">
							<>{t('login.welcome-back')}</>
						</Text>
						<Text category="s1" status="primary" style={styles.subtitleWeb}>
							{t('login.to-keep-connected-with-us-please-login-to-your-account')}
						</Text>
					</>
				) : null}
				<FormInput
					testID="email"
					{...Platform.select({
						default: {
							accessoryLeft: UserIcon,
							placeholder: `${t('common.e-mail')} ${t('common.or')} ${t('common.username')}`,
						},
						web: {
							accessoryLeft: undefined,
							label: `${t('common.e-mail')} ${t('common.or')} ${t('common.username')}`,
							placeholder: `${t('common.enter-your-email')} ${t('common.or')} ${t(
								'common.username',
							)}`,
						},
					})}
					gutter
					textContentType="emailAddress"
					name="emailOrUsername"
					autoFocus
				/>
				<FormInput
					InputComponent={Password}
					testID="password"
					{...Platform.select({
						web: {
							accessoryLeft: undefined,
							label: t('common.password'),
							placeholder: t('common.enter-your-password'),
						},
					})}
					gutter
					name="password"
					onSubmitEditing={onSubmit}
				/>

				<Button size="small" variant="link" style={styles.forgot}>
					<Link screen="ForgotPassword">
						<Text category="c2" style={styles.forgotText}>
							{t('login.forgot-password')}
						</Text>
					</Link>
				</Button>
				<Button
					testID="login-submit"
					loading={isSending}
					color="primary"
					variant="filled"
					onPress={onSubmit}
				>
					{t('login.sign-in')}
				</Button>

				{REGISTRATION_ENABLED && (
					<Text
						style={styles.toSignup}
						align="center"
						category={Platform.select({ web: 's1', default: undefined })}
					>
						{`${t('login.dont-have-an-account')} `}
						<Link
							screen="Register"
							style={styles.toSignupLink}
							action={StackActions.replace('Register')}
						>
							<Text category={Platform.select({ web: 's2', default: undefined })}>
								{t('login.sign-up')}
							</Text>
						</Link>
					</Text>
				)}
			</FormProvider>
		</AuthContainer>
	);
}

const styles = StyleSheet.create({
	forgot: {
		marginBottom: spacing(5.5),
		alignSelf: 'flex-end',
	},
	forgotText: {
		textDecorationLine: 'underline',
		textDecorationColor: colors.primarySemiTransparent,
		...Platform.select({
			web: {
				textDecorationLine: 'none',
			},
		}),
	},
	toSignup: {
		color: colors.primarySemiTransparent,
		marginTop: spacing(5),
	},
	toSignupLink: {
		color: colors.primary,
		marginLeft: spacing(),
	},
	subtitleWeb: {
		paddingBottom: spacing(2.5),
		marginTop: spacing(2),
	},
});

export default withTranslation(Login);
